import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Trash, ArrowsClockwise, ClockCounterClockwise, CaretDown, CaretRight, WarningCircle, X, ShoppingCart, Copy, Download } from '@phosphor-icons/react';
import { computeBundleTotals, computeOrderTotals, groupSelections } from '../lib/bundleHelpers';
import { useAuth } from '../auth/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderRow = ({ order, isOpen, onToggle, onLoad, onDelete, onDuplicate, onAddToCart, busy, isCustomer }) => {
  const totals = computeOrderTotals(order.bundles || []);
  const created = formatNzDateTime(order.createdAt);
  return (
    <div
      className="bg-black/40 border border-border-color rounded-lg overflow-hidden"
      data-testid={`saved-order-${order.id}`}
    >
      <div className="p-4 flex items-center justify-between gap-4">
        <button
          onClick={() => onToggle(order.id)}
          className="flex-1 min-w-0 flex items-start gap-3 text-left"
          data-testid={`toggle-order-${order.id}`}
        >
          <span className="mt-1 text-text-secondary">
            {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{order.name}</p>
            <p className="text-xs text-text-secondary mt-1">
              {order.bundleCount} saver{order.bundleCount === 1 ? '' : 's'} •
              <span className="ml-1">Subtotal <span className="text-text-primary">${totals.subtotal.toFixed(2)}</span></span>
              <span className="mx-1">·</span>
              Discount <span className="text-success">${totals.totalDiscount.toFixed(2)}{!isCustomer && ` (${totals.totalDiscountPct.toFixed(0)}%)`}</span>
              <span className="mx-1">·</span>
              Order Total <span className="text-gold font-semibold">${totals.discountedTotal.toFixed(2)}</span>
            </p>
            <p className="text-[10px] text-text-secondary mt-0.5">Saved {created}</p>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onAddToCart(order)}
            disabled={busy}
            data-testid={`add-order-to-cart-${order.id}`}
            className="flex items-center gap-2 px-3 py-2 border border-gold text-gold text-sm font-semibold rounded-lg hover:bg-gold hover:text-black disabled:opacity-50"
            title="Re-load these savers and add to cart"
          >
            <ShoppingCart size={16} weight="bold" />
            Add to cart
          </button>
          <button
            onClick={() => onLoad(order)}
            disabled={busy}
            data-testid={`load-order-${order.id}`}
            className="flex items-center gap-2 px-3 py-2 bg-gold text-black text-sm font-semibold rounded-lg hover:bg-gold-hover disabled:opacity-50"
          >
            <ArrowsClockwise size={16} weight="bold" />
            Load
          </button>
          <button
            onClick={() => onDuplicate(order)}
            disabled={busy}
            data-testid={`duplicate-order-${order.id}`}
            className="p-2 text-text-secondary hover:text-gold disabled:opacity-50"
            aria-label="Duplicate saved order"
            title="Clone this order into a new Saved Order"
          >
            <Copy size={16} weight="bold" />
          </button>
          {!isCustomer && (
            <div className="flex items-center gap-1 pl-2 ml-1 border-l border-border-color">
              <a
                href={`${API}/saved-orders/${order.id}/download?format=bundle`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`download-saved-bundle-${order.id}`}
                className="px-2 py-1.5 text-xs text-text-secondary border border-border-color rounded hover:border-gold hover:text-gold inline-flex items-center gap-1"
                title="Download bundle CSV (right-click → 'Save link as' if your browser doesn't start the download)"
              >
                <Download size={12} weight="bold" /> Bundle
              </a>
              <a
                href={`${API}/saved-orders/${order.id}/download?format=inventory`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`download-saved-inventory-${order.id}`}
                className="px-2 py-1.5 text-xs text-text-secondary border border-border-color rounded hover:border-gold hover:text-gold inline-flex items-center gap-1"
                title="Download inventory-system CSV (right-click → 'Save link as' if your browser doesn't start the download)"
              >
                <Download size={12} weight="bold" /> Inventory
              </a>
            </div>
          )}
          <button
            onClick={() => onDelete(order)}
            disabled={busy}
            data-testid={`delete-order-${order.id}`}
            className="p-2 text-text-secondary hover:text-danger disabled:opacity-50"
            aria-label="Delete saved order"
            title="Delete this saved order"
          >
            <Trash size={16} weight="bold" />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="px-4 pb-4 pt-1 border-t border-border-color text-xs space-y-3">
          {(order.bundles || []).map((b) => {
            const bt = computeBundleTotals(b);
            return (
              <div key={b.id} className="bg-black/40 rounded p-3" data-testid={`saved-order-${order.id}-bundle-${b.number}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs">
                    <span className="font-mono text-gold mr-2">Saver {String(b.number).padStart(2, '0')}</span>
                    <span className="text-text-primary font-semibold">{b.name}</span>
                  </span>
                  <span className="text-text-secondary">
                    Subtotal <span className="text-text-primary">${bt.subtotal.toFixed(2)}</span>
                    <span className="mx-1">·</span>
                    Saver Price <span className="text-gold font-semibold">${bt.bundlePrice.toFixed(2)}</span>
                  </span>
                </div>
                {groupSelections(b.products).map((g) => {
                  const unit = Math.round(((typeof g.unitPrice === 'number' && Number.isFinite(g.unitPrice))
                    ? g.unitPrice : Number(g.wholesalePrice || 0)) * 100) / 100;
                  return (
                    <div key={`${g.id}-${g.selectedFlavour || 'def'}`} className="flex justify-between text-text-primary">
                      <span className="truncate pr-2">
                        {g.name}
                        {g.selectedFlavour && <span className="text-gold"> – {g.selectedFlavour}</span>}
                        {g.qty > 1 && <span className="ml-1 text-gold font-semibold">× {g.qty}</span>}
                      </span>
                      <span className="text-text-secondary shrink-0">${(unit * g.qty).toFixed(2)}</span>
                    </div>
                  );
                })}
                {b.comments && (
                  <div className="mt-2 bg-black/40 rounded p-2 text-text-secondary italic whitespace-pre-wrap">
                    {b.comments}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const SavedOrdersPage = () => {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [allCustomers, setAllCustomers] = useState([]);
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [hasActiveBundles, setHasActiveBundles] = useState(false);
  const [loadModeOrder, setLoadModeOrder] = useState(null);   // order awaiting replace/append choice
  const [deleteOrder, setDeleteOrder] = useState(null);       // order awaiting delete confirmation

  const targetCustomerId = useMemo(() => {
    if (!me) return null;
    return me.role === 'customer' ? me.id : activeCustomerId;
  }, [me, activeCustomerId]);

  const fetchCustomers = useCallback(async () => {
    if (!me || me.role === 'customer') return;
    try {
      const res = await axios.get(`${API}/users`);
      setAllCustomers(res.data.filter((u) => u.role === 'customer' && u.active));
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  }, [me]);

  const fetchOrders = useCallback(async () => {
    if (!targetCustomerId) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/saved-orders?customerId=${targetCustomerId}`);
      setOrders(res.data);
    } catch (e) {
      console.error('Error fetching saved orders:', e);
    } finally {
      setLoading(false);
    }
  }, [targetCustomerId]);

  const fetchActiveBundles = useCallback(async () => {
    if (!targetCustomerId) {
      setHasActiveBundles(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/saved-bundles?customerId=${targetCustomerId}`);
      setHasActiveBundles((res.data || []).length > 0);
    } catch (e) {
      console.error('Error fetching active bundles:', e);
    }
  }, [targetCustomerId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchOrders();
    fetchActiveBundles();
  }, [fetchOrders, fetchActiveBundles]);

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLoad = (order) => {
    if (!targetCustomerId) return;
    if (hasActiveBundles) {
      setLoadModeOrder(order); // open 3-way modal — user picks Replace / Append / Cancel
      return;
    }
    runLoad(order, 'replace');
  };

  const runLoad = async (order, mode) => {
    setLoadModeOrder(null);
    setBusy(true);
    setMsg(null);
    try {
      const res = await axios.post(`${API}/saved-orders/${order.id}/load?mode=${mode}`);
      setMsg({ kind: 'ok', text: `Loaded ${res.data.loaded} saver${res.data.loaded === 1 ? '' : 's'} (${mode}). Redirecting…` });
      setTimeout(() => navigate('/'), 800);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Load failed' });
      setTimeout(() => setMsg(null), 5000);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = (order) => {
    setDeleteOrder(order);
  };

  const handleDuplicate = async (order) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await axios.post(`${API}/saved-orders/${order.id}/duplicate`);
      await fetchOrders();
      setMsg({ kind: 'ok', text: `Duplicated as "${res.data?.name}".` });
      setTimeout(() => setMsg(null), 5000);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Duplicate failed' });
      setTimeout(() => setMsg(null), 5000);
    } finally {
      setBusy(false);
    }
  };

  // Brave (and Firefox with strict popup settings) silently block programmatic
  // downloads from within the Emergent preview's sandboxed iframe — plain
  // anchors only work via right-click → "Save link as" there. Direct clicks
  // download normally in the deployed production app (no iframe sandbox).
  // The hint banner near the top of the page tells users about the
  // right-click workaround.

  const handleAddToCart = async (order) => {
    if (!targetCustomerId) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await axios.post(
        `${API}/cart/from-saved-order/${order.id}?customerId=${targetCustomerId}`,
      );
      const count = (res.data.savers || []).length;
      setMsg({ kind: 'ok', text: `Added ${count} saver${count === 1 ? '' : 's'} to cart. Open Browse & Order to checkout.` });
      setTimeout(() => setMsg(null), 6000);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Add to cart failed' });
      setTimeout(() => setMsg(null), 6000);
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!deleteOrder) return;
    const order = deleteOrder;
    setDeleteOrder(null);
    setBusy(true);
    try {
      await axios.delete(`${API}/saved-orders/${order.id}`);
      await fetchOrders();
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Delete failed' });
      setTimeout(() => setMsg(null), 5000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-2 flex items-center gap-3" data-testid="saved-orders-title">
              <ClockCounterClockwise size={32} weight="duotone" className="text-gold" />
              Saved Orders
            </h1>
            <p className="text-text-secondary">Reuse a previously saved order — load all of its bundles back into the builder.</p>
          </div>
          {me && me.role !== 'customer' && (
            <div className="flex flex-col shrink-0" data-testid="orders-customer-selector-wrap">
              <label className="text-xs text-text-secondary mb-1">Viewing orders for</label>
              <select
                value={activeCustomerId || ''}
                onChange={(e) => setActiveCustomerId(e.target.value || null)}
                data-testid="orders-customer-selector"
                className="px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none min-w-[220px]"
              >
                <option value="">— Select a customer —</option>
                {allCustomers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {msg && (
          <div
            data-testid="saved-orders-status"
            className={`mb-4 text-sm px-3 py-2 rounded ${msg.kind === 'ok' ? 'text-success bg-success/10 border border-success/30' : 'text-danger bg-danger/10 border border-danger/30'}`}
          >
            {msg.text}
          </div>
        )}

        {me?.role !== 'customer' && !targetCustomerId && (
          <p className="text-text-secondary italic" data-testid="orders-pick-customer-empty">Pick a customer above to view their saved orders.</p>
        )}

        {targetCustomerId && loading && <p className="text-text-secondary">Loading…</p>}
        {targetCustomerId && !loading && orders.length === 0 && (
          <div className="bg-surface border border-border-color rounded-xl p-8 text-center" data-testid="saved-orders-empty">
            <p className="text-text-primary font-semibold mb-2">No saved orders yet</p>
            <p className="text-text-secondary text-sm">
              Build some savers in Raisey&apos;s Smart Saver and click <span className="text-gold font-semibold">Save Order</span> to snapshot them here for reuse.
            </p>
          </div>
        )}

        {targetCustomerId && !loading && orders.length > 0 && (
          <>
            {me?.role !== 'customer' && (
              <div
                className="mb-3 px-3 py-2 text-xs text-text-secondary bg-black/30 border border-dashed border-border-color rounded"
                data-testid="saved-orders-download-hint"
              >
                💡 If <strong className="text-text-primary">Bundle/Inventory</strong> open a blank tab instead of downloading
                (some browsers like Brave block this inside the preview iframe),
                <strong className="text-text-primary"> right-click the button → &ldquo;Save link as&rdquo;</strong>.
                Direct-click downloads work normally in the deployed production app.
              </div>
            )}
            <div className="space-y-3" data-testid="saved-orders-list">
              {orders.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  isOpen={openIds.has(o.id)}
                  onToggle={toggleOpen}
                  onLoad={handleLoad}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onAddToCart={handleAddToCart}
                  busy={busy}
                  isCustomer={me?.role === 'customer'}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Load mode picker — 3-way dialog: Replace / Append / Cancel */}
      {loadModeOrder && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="load-mode-dialog"
          onClick={() => !busy && setLoadModeOrder(null)}
        >
          <div
            className="bg-surface border border-border-color rounded-xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5 border-b border-border-color">
              <WarningCircle size={22} weight="duotone" className="text-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-text-primary">Active bundles already in the builder</h3>
                <p className="text-sm text-text-secondary mt-1">
                  How should we load <strong className="text-gold">{loadModeOrder.name}</strong>?
                </p>
                <ul className="text-xs text-text-secondary mt-2 space-y-1">
                  <li><strong className="text-text-primary">Replace</strong> — clear current bundles and load this order&apos;s bundles.</li>
                  <li><strong className="text-text-primary">Append</strong> — keep current bundles and add this order&apos;s bundles after them.</li>
                </ul>
              </div>
              <button
                onClick={() => setLoadModeOrder(null)}
                disabled={busy}
                data-testid="load-mode-close"
                className="text-text-secondary hover:text-text-primary disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 p-4">
              <button
                onClick={() => setLoadModeOrder(null)}
                disabled={busy}
                data-testid="load-mode-cancel"
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={() => runLoad(loadModeOrder, 'append')}
                disabled={busy}
                data-testid="load-mode-append"
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-border-color text-text-primary hover:bg-surface-hover disabled:opacity-50"
              >Append</button>
              <button
                onClick={() => runLoad(loadModeOrder, 'replace')}
                disabled={busy}
                data-testid="load-mode-replace"
                className="px-4 py-2 text-sm font-bold rounded-lg bg-gold text-black hover:bg-gold-hover disabled:opacity-50"
              >Replace</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteOrder}
        title="Delete saved order?"
        message={deleteOrder ? `Delete "${deleteOrder.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        busy={busy}
        onCancel={() => setDeleteOrder(null)}
        onConfirm={runDelete}
        testIdPrefix="confirm-delete-order"
      />
    </div>
  );
};

export default SavedOrdersPage;
